import { getOpenLink } from "@skanska/vsa-file-icon";
import { UserInformation } from "@skanska/vsa-people-picker";
import { FieldValues } from "../models/FieldValues";
import { Tool, ToolTypes } from "../models/Tool";

export const searchTools = (query: string, tools: Tool[]) => {
  const normalizedQuery = query.toLowerCase();

  const foundTools: Tool[] = tools.filter((a) =>
    queryFoundInTool(normalizedQuery, a)
  );

  return foundTools;
};

const queryFoundInTool = (normalizedQuery: string, tool: Tool) => {
  if (propValueContainsQuery(normalizedQuery, tool.title)) {
    return true;
  }

  return false;
};

const propValueContainsQuery = (normalizedQuery: string, propValue: string) => {
  return propValue.toLowerCase().indexOf(normalizedQuery) !== -1;
};

export const sanitizeFileName = (rawUrl: string): string => {
  if (!rawUrl) return "";

  const decoded = getFileNameSegment(rawUrl);
  if (!decoded) return "";

  const match = decoded.match(/^(.+?)(\.[^./]+)$/);
  if (!match) {
    // no extension: sanitize all
    return decoded.trim().replace(/\s+/g, "_");
  }

  const [, baseMatch, ext] = match;
  const base = baseMatch // base is let so we can reassign
    .replace(/([_-]v?\d+)$/, ""); // strip version suffixes like "_1", "_2", etc.

  // Remove whitespace or underscore
  const safeBase = base.trim().replace(/\s+/g, "_");
  return `${safeBase}${ext.toLowerCase()}`;
};

export const getFileNameWithoutExtension = (
  rawFileNameOrUrl?: string | null
): string => {
  if (!rawFileNameOrUrl) return "";

  const decoded = getFileNameSegment(rawFileNameOrUrl);
  if (!decoded) return "";

  return decoded.replace(/\.[^/.]+$/, "");
};

export const getFileExtension = (rawFileNameOrUrl?: string | null): string => {
  if (!rawFileNameOrUrl) return "";

  const decoded = getFileNameSegment(rawFileNameOrUrl);
  if (!decoded) return "";

  const match = decoded.match(/(\.[^./]+)$/);

  return match?.[1] ?? "";
};

const getFileNameSegment = (rawFileNameOrUrl: string): string => {
  const trimmedValue = rawFileNameOrUrl.trim();

  if (/^https?:\/\//i.test(trimmedValue)) {
    try {
      const url = new URL(trimmedValue);
      const fileNameFromQuery = url.searchParams.get("file");

      if (fileNameFromQuery) {
        return fileNameFromQuery;
      }

      const pathSegment = url.pathname.split(/[/\\]/).pop();
      return decodeFileNameSegment(pathSegment ?? "");
    } catch {
      return getPlainFileNameSegment(trimmedValue);
    }
  }

  return getPlainFileNameSegment(trimmedValue);
};

const getPlainFileNameSegment = (rawFileNameOrUrl: string): string => {
  const lastSegment = rawFileNameOrUrl.split(/[/\\]/).pop();
  if (!lastSegment) return "";

  return decodeFileNameSegment(lastSegment.split(/[?#]/)[0]);
};

const decodeFileNameSegment = (segment: string): string => {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
};

export const getToolUrl = (url: string): string => {
  if (url.startsWith("/")) {
    return `${import.meta.env.VITE_CLIENT_APP_URL}${url}`;
  }

  return url;
};

export const getToolOpenUrl = (tool: Tool): string => {
  const toolUrl = getToolUrl(tool.webUrl);

  if (!tool.fileUniqueId || !tool.fileType) {
    return toolUrl;
  }

  return getOpenLink({
    webUrl: toolUrl,
    fileType: tool.fileType,
  });
};

export const getToolFileName = (tool: Tool): string =>
  tool.fileName || getFileNameWithoutExtension(tool.webUrl);

// Merges the tools metadata fields with the metadata fields of the associated activities.
export const mergeToolsFieldsWithAssociatedActivitiesFields = (
  tools: Tool[]
): Tool[] => {
  return tools.map((tool) => {
    // tool's own fields
    const merged: FieldValues = {
      tags: tool.fieldValues ? tool.fieldValues.tags : {},
      booleans: tool.fieldValues ? tool.fieldValues.booleans : {},
      users: tool.fieldValues ? tool.fieldValues.users : {},
      urls: tool.fieldValues ? tool.fieldValues.urls : {},
    };

    // add activity's fields (excluding 'active' field to maintain tool's independent status)
    (tool.activities || []).forEach((act) => {
      if (!act.fieldValues) {
        return;
      }

      Object.entries(act.fieldValues.tags).forEach(([key, val]) => {
        if (!val || val.length === 0) {
          return;
        }

        const existing = merged.tags[key] ? merged.tags[key] : [];

        const combined = [...existing, ...val];
        const unique = Array.from(
          new Set(combined.map((x) => JSON.stringify(x)))
        ).map((s) => JSON.parse(s));

        merged.tags[key] = unique;
      });

      Object.entries(act.fieldValues.booleans ?? {}).forEach(([key, val]) => {
        if (val != null && key !== "active") {
          // Booleans should always be booleans.
          if (val === undefined || val === null) {
            return;
          }

          merged.booleans[key] = val;
        }
      });

      Object.entries(act.fieldValues.users ?? {}).forEach(([key, val]) => {
        if (!val || val.length === 0) {
          return;
        }

        const existing = Array.isArray(merged.users[key])
          ? (merged.users[key] as UserInformation[])
          : [];

        const combined = [...existing, ...val];
        const unique = Array.from(
          new Set(combined.map((x) => JSON.stringify(x)))
        ).map((s) => JSON.parse(s));

        merged.users[key] = unique;
      });
    });

    return {
      ...tool,
      fieldsWithValues: merged,
    };
  });
};

const toolIsDocument = (tool: Tool): boolean => {
  // If fileUniqueId is present, it's a document
  if (tool.fileUniqueId) {
    return true;
  }
  return false;
};

export const getToolType = (tool: Tool): ToolTypes => {
  if (toolIsDocument(tool)) {
    return ToolTypes.Document;
  }
  return ToolTypes.Link;
};
